import React, { useEffect, useState } from 'react';
import { useCart } from '../../context/CartContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function ProductDetail({ product, onClose }) {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { dispatch } = useCart();

  useEffect(() => {
    if (product) {
      setLoading(true);
      fetch(`${API}/products/${product.id}/story`)
        .then(r => r.json())
        .then(data => { setStory(data); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [product]);

  if (!product) return null;

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      dispatch({ type: 'ADD', product });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-[#FEFCF8] w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Large product image */}
        <div className="relative aspect-[16/10] bg-[#F3DFC0]">
          {product.image_url && (
            <img
              src={product.image_url.replace('w=400&h=300', 'w=800&h=500')}
              alt={product.nom}
              className={`w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
            />
          )}
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center text-[#332A1C] hover:bg-white transition"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {/* Stand badge */}
          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-[#8B7355]">
            {product.stand_nom}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Title and price */}
          <div className="flex items-start justify-between gap-4 mb-2">
            <h2 className="font-serif text-2xl text-[#332A1C]">{product.nom}</h2>
            <span className="font-serif text-2xl font-bold text-[#C4673D] whitespace-nowrap">
              {product.prix_ttc.toFixed(2)} €
            </span>
          </div>

          <p className="text-[#8B7355] text-sm mb-4">{product.categorie}</p>

          {/* Tags */}
          {story && story.tags && story.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {story.tags.map((tag, i) => (
                <span key={i} className="bg-[#F3DFC0]/60 text-[#8B7355] text-xs px-3 py-1 rounded-full border border-[#E2D1B8]">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Story / Loading */}
          {loading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-4 bg-[#F3DFC0] rounded w-full" />
              <div className="h-4 bg-[#F3DFC0] rounded w-5/6" />
              <div className="h-4 bg-[#F3DFC0] rounded w-4/6" />
            </div>
          ) : story ? (
            <div className="space-y-5">
              {/* Histoire */}
              {story.histoire && (
                <div>
                  <h3 className="font-serif text-lg text-[#332A1C] mb-2">L'histoire</h3>
                  <p className="text-[#8B7355] text-sm leading-relaxed italic">
                    "{story.histoire}"
                  </p>
                </div>
              )}

              {/* Préparation */}
              {story.preparation && (
                <div>
                  <h3 className="font-serif text-lg text-[#332A1C] mb-2">Préparation</h3>
                  <p className="text-[#8B7355] text-sm leading-relaxed">{story.preparation}</p>
                </div>
              )}

              {/* Ingrédients */}
              {story.ingredients && story.ingredients.length > 0 && (
                <div>
                  <h3 className="font-serif text-lg text-[#332A1C] mb-2">Ingrédients</h3>
                  <div className="flex flex-wrap gap-2">
                    {story.ingredients.map((ing, i) => (
                      <span key={i} className="bg-white border border-[#E2D1B8] text-[#332A1C] text-xs px-3 py-1.5 rounded-lg">
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Conseil d'accord */}
              {story.conseil && (
                <div className="bg-[#C4673D]/5 border border-[#C4673D]/20 rounded-xl p-4">
                  <p className="text-sm text-[#C4673D]">
                    <span className="font-semibold">💡 Conseil du chef : </span>
                    {story.conseil}
                  </p>
                </div>
              )}

              {/* Allergènes */}
              {story.allergenes && story.allergenes.length > 0 && (
                <div>
                  <p className="text-xs text-[#8B7355]">
                    <span className="font-medium">Allergènes possibles : </span>
                    {story.allergenes.join(', ')}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-[#8B7355] text-sm italic">{product.description}</p>
          )}

          {/* Quantity selector + Add to cart */}
          <div className="mt-6 pt-4 border-t border-[#F3DFC0]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full border border-[#E2D1B8] text-[#8B7355] font-bold flex items-center justify-center hover:border-[#C4673D] hover:text-[#C4673D] transition"
                >−</button>
                <span className="text-lg font-medium text-[#332A1C] w-6 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full border border-[#E2D1B8] text-[#8B7355] font-bold flex items-center justify-center hover:border-[#C4673D] hover:text-[#C4673D] transition"
                >+</button>
              </div>
              <button
                onClick={handleAddToCart}
                className="bg-[#C4673D] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#A8532E] transition-colors shadow-sm"
              >
                Ajouter · {(product.prix_ttc * quantity).toFixed(2)} €
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
